import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Upload, Download, Trash2, File, FileText, FileImage, FileVideo } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiUpload, apiDelete, apiPatch } from '../../../api/client';
import type { Event, Attachment } from '../../../api/types';
import { Button } from '../../../components/ui/Button';
import { EmptyState } from '../../../components/ui/EmptyState';
import { ConfirmDialog } from '../../../components/ui/Modal';
import { formatDateTime } from '../../../utils/format';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function FileIcon({ mimeType }: { mimeType: string }) {
  if (mimeType.startsWith('image/')) return <FileImage size={18} style={{ color: 'var(--color-primary)' }} />;
  if (mimeType.startsWith('video/')) return <FileVideo size={18} style={{ color: 'var(--color-warning)' }} />;
  if (mimeType === 'application/pdf') return <FileText size={18} style={{ color: 'var(--color-error)' }} />;
  return <File size={18} style={{ color: 'var(--color-text-muted)' }} />;
}

export default function FilesTab({ event }: { event: Event }) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [notes, setNotes] = useState(event.notes ?? '');
  const [saving, setSaving] = useState(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout>>();

  const { data: attachments = [] } = useQuery<Attachment[]>({
    queryKey: ['attachments', event.id],
    queryFn: () => apiGet<Attachment[]>(`/events/${event.id}/attachments`),
  });

  // Autosave notes
  const saveNotes = useCallback(async (value: string) => {
    setSaving(true);
    try {
      await apiPatch(`/events/${event.id}`, { notes: value });
      qc.invalidateQueries({ queryKey: ['event', event.id] });
    } catch { /* ignore */ }
    setSaving(false);
  }, [event.id, qc]);

  useEffect(() => {
    clearTimeout(saveTimer.current);
    if (notes !== (event.notes ?? '')) {
      saveTimer.current = setTimeout(() => saveNotes(notes), 2000);
    }
    return () => clearTimeout(saveTimer.current);
  }, [notes, event.notes, saveNotes]);

  const uploadMutation = useMutation({
    mutationFn: (files: FileList) => {
      const fd = new FormData();
      Array.from(files).forEach(f => fd.append('files', f));
      return apiUpload<Attachment[]>(`/events/${event.id}/attachments`, fd);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['attachments', event.id] }); toast.success('File caricato'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/attachments/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['attachments', event.id] }); setDeleteTarget(null); toast.success('File eliminato'); },
  });

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files.length) uploadMutation.mutate(e.dataTransfer.files);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* Notes */}
      <div style={{
        background: 'var(--glass-bg-card)',
        border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: 'var(--radius-card)',
        padding: '1.25rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
          <h3 style={{ fontSize: 'var(--text-md)', fontWeight: 600 }}>Note interne</h3>
          {saving && <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>Salvataggio…</span>}
        </div>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Note interne, dettagli, richieste speciali… (Markdown supportato, autosalvataggio)"
          style={{
            background: 'var(--glass-bg-card)',
            border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: 'var(--radius-card)',
            color: 'var(--color-text)',
            fontSize: 'var(--text-sm)',
            padding: '0.75rem',
            width: '100%',
            minHeight: 160,
            resize: 'vertical',
            outline: 'none',
            fontFamily: 'JetBrains Mono, monospace',
            lineHeight: 1.6,
          }}
        />
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', marginTop: '0.375rem' }}>
          Autosalvataggio dopo 2 secondi
        </p>
      </div>

      {/* Upload */}
      <div
        onDragOver={e => e.preventDefault()}
        onDrop={handleDrop}
        onClick={() => fileRef.current?.click()}
        style={{
          border: '2px dashed rgba(255,255,255,0.10)',
          borderRadius: 'var(--radius-card)',
          padding: '1.5rem',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '0.5rem',
          cursor: 'pointer',
          background: 'var(--glass-bg-card)',
          transition: 'all var(--transition-interactive)',
        }}
        onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--color-primary)')}
        onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.10)')}
      >
        <Upload size={22} style={{ color: 'var(--color-text-muted)' }} />
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
          {uploadMutation.isPending ? 'Caricamento…' : 'Trascina file qui o clicca per scegliere (max 50MB)'}
        </p>
        <input ref={fileRef} type="file" multiple style={{ display: 'none' }}
          onChange={e => { if (e.target.files?.length) uploadMutation.mutate(e.target.files); }} />
      </div>

      {/* Attachments list */}
      {attachments.length > 0 && (
        <div style={{
          background: 'var(--glass-bg-card)',
          border: '1px solid rgba(255,255,255,0.10)',
          borderRadius: 'var(--radius-card)',
          overflow: 'hidden',
        }}>
          {attachments.map((att, i) => (
            <div key={att.id} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '0.875rem 1rem',
              borderBottom: i < attachments.length - 1 ? '1px solid rgba(255,255,255,0.08)' : 'none',
              gap: '1rem',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: 0 }}>
                <FileIcon mimeType={att.mimeType} />
                <div style={{ minWidth: 0 }}>
                  <p style={{ fontSize: 'var(--text-sm)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.name}</p>
                  <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                    {formatBytes(att.size)} · {formatDateTime(att.createdAt)}
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                <a
                  href={`/api/attachments/${att.id}/download`}
                  download
                  onClick={e => e.stopPropagation()}
                  style={{
                    display: 'flex', alignItems: 'center', padding: '0.25rem 0.5rem',
                    background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
                    borderRadius: 'var(--radius-interactive)', color: 'var(--color-text)',
                    textDecoration: 'none', fontSize: 'var(--text-xs)',
                  }}
                >
                  <Download size={13} />
                </a>
                <button
                  onClick={() => setDeleteTarget(att.id)}
                  aria-label="Elimina"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)', display: 'flex', padding: '0.25rem' }}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {attachments.length === 0 && !uploadMutation.isPending && (
        <EmptyState icon={<Upload size={32} />} title="Nessun allegato" description="Carica contratti, brief o altri documenti" />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget)}
        title="Elimina file"
        description="Il file verrà eliminato definitivamente. Questa azione non è reversibile."
        confirmLabel="Elimina"
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
